
import { applyEventThemeVars } from "@/lib/utils/eventTheme";
import {
  createContext,
  memo,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import Sidebar from "./Sidebar";
import Topbar from "./Topbar";

import ErrorBoundary from "@/components/common/ErrorBoundary";
import RequestModal from "@/components/register-requests/RequestModal";
import { apolloClient } from "@/lib/apollo/client";
import { GET_EVENT } from "@/graphql/queries/events";
import { useEventsFeature } from "@/features/events/useEventsFeature";
import { useRequestToJoin } from "@/graphql/hooks/useEvents";
import { useAppSelector } from "@/store/hooks";
import { useAppDispatch } from "@/store/hooks";
import { setAttendeeStatus } from "@/store/slices/userSlice";
import { Button } from "../ui";
import { ROUTES } from "@/constants";

/** Same spinner used by the shell gates, so a refresh shows one loading state. */
function PageSpinner() {
  return (
    <div className="flex h-full min-h-[50vh] items-center justify-center">
      <div
        className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 border-t-[var(--sidebar-bg)] text-center"
        aria-label="Loading page..."
      />
    </div>
  );
}

// ─── Sidebar context — lets Topbar toggle without prop drilling ────
interface SidebarContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export const SidebarContext = createContext<SidebarContextValue>({
  isOpen: false,
  open: () => undefined,
  close: () => undefined,
  toggle: () => undefined,
});

export const useSidebar = () => useContext(SidebarContext);

// ─── AppShell ─────────────────────────────────────────────────────
interface AppShellProps {
  children: ReactNode;
}

/**
 * Root authenticated layout — responsive.
 *
 * Desktop (lg+):
 *   ┌──────────┬────────────────────────────────┐
 *   │          │  Topbar                         │
 *   │ Sidebar  ├────────────────────────────────┤
 *   │          │  <page>                         │
 *   └──────────┴────────────────────────────────┘
 *
 * Mobile (<lg):
 *   ┌────────────────────────────────────────────┐
 *   │  Topbar  [☰]                               │
 *   ├────────────────────────────────────────────┤
 *   │  <page>                                    │
 *   └────────────────────────────────────────────┘
 *   Sidebar slides in from the left as a drawer overlay.
 */
const AppShell = memo(function AppShell({ children }: AppShellProps) {
  const currentUser = useAppSelector((s) => s.user.currentUser);

  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const ctx = useMemo(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  );

  const [isTrialOpen, setIsTrialOpen] = useState(false);
  const [initialSubmittedFor, setInitialSubmittedFor] = useState<
    "Myself" | "Family Member"
  >("Myself");

  const { openCurrentEvent, currentEvent } = useEventsFeature();

  const pathname = window.location.pathname;
  const slug = pathname.split("/")[1] ?? "";
  const themeCacheKey = `eventTheme:${slug}`;

  // On first visit there is no cached theme, so hide the sidebar until the event theme loads
  // (or a short timeout passes) instead of flashing the default colour.
  const [themePending, setThemePending] = useState(() => {
    try {
      return !localStorage.getItem(themeCacheKey);
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!themePending) return;
    if (currentEvent?.theme) {
      setThemePending(false);
      return;
    }
    const timer = window.setTimeout(() => setThemePending(false), 1500);
    return () => window.clearTimeout(timer);
  }, [themePending, currentEvent?.theme]);

  // Apply the last-known theme before paint so the default sidebar colour doesn't flash on reload
  useLayoutEffect(() => {
    try {
      const cached = localStorage.getItem(themeCacheKey);
      if (cached) applyEventThemeVars(JSON.parse(cached));
    } catch {
      // Ignore unavailable storage or corrupt cache entries
    }
  }, [themeCacheKey]);

  useEffect(() => {
    // Wait for the event so the cached theme isn't overwritten with defaults on first render
    if (!currentEvent) return;
    applyEventThemeVars(currentEvent.theme);
    if (currentEvent.theme) {
      try {
        localStorage.setItem(themeCacheKey, JSON.stringify(currentEvent.theme));
      } catch {
        // Storage may be full or disabled; theme still applies live
      }
    }
  }, [currentEvent?.theme, themeCacheKey]);

  const {
    joinState,
    loading: requestToJoinLoading,
    error: requestToJoinError,
  } = useRequestToJoin(currentEvent?.id ?? slug, Boolean(currentUser?.id));
  const attendee = joinState?.requests.find(
    (request) => request.requesterId === currentUser?.id,
  );
  const attendeeStatus = attendee?.status?.toUpperCase();
  const isLeague = String(currentEvent?.platformEventType ?? "").includes(
    "LEAGUE",
  );
  // League organisers manage the league and do not need to be a club member to see it.
  const canAccessEvent =
    joinState?.hasAccess === true ||
    (isLeague && joinState?.canManageRequests === true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    // persist attendee status to redux — only after requests have been fetched
    if (requestToJoinLoading) return;
    dispatch(setAttendeeStatus(attendeeStatus ?? null));
  }, [attendeeStatus, dispatch, requestToJoinLoading]);

  useEffect(() => {
    // Fetch event details from the server but delay committing the
    // event into app state until the joinState (bootstrap) has settled.
    // This prevents a brief window where currentEvent grants access
    // before the server-authoritative joinState is available.
    (async () => {
      try {
        const { data } = await apolloClient.query({
          query: GET_EVENT,
          variables: {
            eventID: slug,
          },
          fetchPolicy: "network-only",
        });

        // Wait until joinState finishes loading (or is skipped) before
        // calling openCurrentEvent which writes currentEvent into the
        // global feature/store. This avoids transient permission leaks.
        if (requestToJoinLoading) return;

        openCurrentEvent(data?.getEvent);
      } catch (error) {
        console.error("GET_EVENT error", error);
      }
    })();
  }, [openCurrentEvent, slug, requestToJoinLoading]);

  // If user landed on the root path, show a brief loading state until the
  // router redirect to `/home` completes to avoid flashing the access page.
  if (pathname === "/") {
    return (
      <div className="flex h-screen items-center justify-center py-20">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 border-t-[var(--sidebar-bg)] text-center"
          aria-label="Redirecting..."
        />
      </div>
    );
  }

  // If we have an active event but the request-to-join list is still
  // resolving, show a loader to avoid briefly rendering the access-denied
  // UI while the query completes.
  if (currentEvent && requestToJoinLoading) {
    return (
      <div className="flex h-screen items-center justify-center py-20">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 border-t-[var(--sidebar-bg)] text-center"
          aria-label="Loading access..."
        />
      </div>
    );
  }

  // Before the join state is known (query skipped until the user loads, or
  // still in flight), don't render the access-denied UI.
  if (!joinState && !requestToJoinError && !canAccessEvent) {
    return (
      <div className="flex h-screen items-center justify-center py-20">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 border-t-[var(--sidebar-bg)] text-center"
          aria-label="Loading access..."
        />
      </div>
    );
  }

  // Hold the shell until the event is committed; otherwise the sidebar and page
  // render empty and then fill in, which looks like ghosting on refresh.
  if (canAccessEvent && !currentEvent && !requestToJoinError) {
    return (
      <div className="flex h-screen items-center justify-center py-20">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 border-t-[var(--sidebar-bg)] text-center"
          aria-label="Loading event..."
        />
      </div>
    );
  }

  if (requestToJoinError) {
    return (
      <p role="alert">
        Unable to load event access: {requestToJoinError.message}
      </p>
    );
  }

  if (isLeague && !canAccessEvent) {
    return (
      <SidebarContext.Provider value={ctx}>
        {pathname.split("/")[2] === "league-onboarding" ? (
          <div className="flex h-screen overflow-hidden bg-neutral-100 dark:bg-neutral-950">
            {isOpen && (
              <div
                className="fixed inset-0 z-[199] bg-black/50 backdrop-blur-sm lg:hidden"
                onClick={close}
                aria-hidden
              />
            )}

            <div
              className={[
                // Base — fixed on mobile, relative slot on desktop
                "fixed inset-y-0 left-0 z-[200]",
                "lg:relative lg:z-auto lg:translate-x-0",
                // Slide transition
                "transition-transform duration-300 ease-in-out",
                isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
              ].join(" ")}
              style={{ width: "var(--sidebar-width)" }}
            >
              <div style={{ display: "contents", visibility: themePending ? "hidden" : undefined }}>
                <Sidebar />
              </div>
            </div>

            <div className="flex min-w-0 flex-1 flex-col">
              <Topbar />

              <main
                id="main-content"
                className="scrollbar-thin flex-1 overflow-y-auto"
              >
                <div className="min-h-full overflow-scroll p-4 sm:p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<PageSpinner />}>{children}</Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </div>
        ) : (
          <div className="flex h-screen flex-col items-center justify-center gap-2 text-center text-neutral-500 dark:text-neutral-400">
            <p>This is a league event. You must be a club to participate.</p>
            <Button
              size="sm"
              type="button"
              onClick={() => {
                window.location.href = `/${slug}${ROUTES.LEAGUE_ONBOARDING}`;
              }}
            >
              Commence Onboarding
            </Button>
          </div>
        )}
      </SidebarContext.Provider>
    );
  }

  return (
    <SidebarContext.Provider value={ctx}>
      <div className="flex h-screen overflow-hidden bg-neutral-100 dark:bg-neutral-950">
        {/* ── Mobile backdrop — tap outside to close ── */}
        {isOpen && (
          <div
            className="fixed inset-0 z-[199] bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={close}
            aria-hidden
          />
        )}

        {/* ── Sidebar ── */}
        {/*
          Desktop: static, always visible (lg:relative lg:translate-x-0).
          Mobile:  fixed overlay, slides in from left when isOpen.
        */}
        {canAccessEvent && (
          <div
            className={[
              // Base — fixed on mobile, relative slot on desktop
              "fixed inset-y-0 left-0 z-[200]",
              "lg:relative lg:z-auto lg:translate-x-0",
              // Slide transition
              "transition-transform duration-300 ease-in-out",
              isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
            ].join(" ")}
            style={{ width: "var(--sidebar-width)" }}
          >
            <div style={{ display: "contents", visibility: themePending ? "hidden" : undefined }}>
              <Sidebar />
            </div>
          </div>
        )}

        {/* ── Main column ── */}
        <div className="flex min-w-0 flex-1 flex-col">
          {canAccessEvent && <Topbar />}

          <main
            id="main-content"
            className="scrollbar-thin flex-1 overflow-y-auto"
          >
            {canAccessEvent ? (
              <div className="min-h-full p-4 sm:p-6">
                <ErrorBoundary>
                  <Suspense fallback={<PageSpinner />}>{children}</Suspense>
                </ErrorBoundary>
              </div>
            ) : (
              <>
                {attendeeStatus === "AWAITING" ? (
                  <div className="flex h-screen min-h-full flex-col items-center justify-center p-4 text-center text-lg font-medium text-neutral-500 dark:text-neutral-400 sm:p-6">
                    Your request is awaiting approval. Please contact the
                    administrator for details.
                    <div className="mt-4">
                      <button
                        className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white"
                        type="button"
                        onClick={() => {
                          setInitialSubmittedFor("Family Member");
                          setIsTrialOpen(true);
                        }}
                      >
                        Request for family member
                      </button>
                    </div>
                  </div>
                ) : attendeeStatus === "DECLINED" ? (
                  <div className="flex h-screen min-h-full flex-col items-center justify-center p-4 text-center text-lg font-medium text-neutral-500 dark:text-neutral-400 sm:p-6">
                    Your request was declined. Please contact the
                    administrator for details.
                  </div>
                ) : attendeeStatus === "PENDING" ? (
                  <div className="flex h-screen min-h-full flex-col items-center justify-center p-4 text-center text-lg font-medium text-neutral-500 dark:text-neutral-400 sm:p-6">
                    Your request is pending approval. Please contact the
                    administrator for details.
                  </div>
                ) : attendeeStatus === "ARCHIVED" ? (
                  <div className="flex h-screen min-h-full flex-col items-center justify-center p-4 text-center text-lg font-medium text-neutral-500 dark:text-neutral-400 sm:p-6">
                    Your request is archived / pending approval. Please contact
                    the administrator for details.
                  </div>
                ) : (
                  <div className="my-auto flex h-screen min-h-full flex-col items-center justify-center p-4 text-center text-lg font-medium text-neutral-500 dark:text-neutral-400 sm:p-6">
                    <p>
                      You do not have access to this event. Click on request to join below.
                    </p>
                    <button
                      className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white"
                      onClick={() => {
                        setInitialSubmittedFor("Myself");
                        setIsTrialOpen(true);
                      }}
                    >
                      Request to join
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
        <RequestModal
          isOpen={isTrialOpen}
          onClose={() => setIsTrialOpen(false)}
          currentUser={currentUser}
          initialSubmittedFor={initialSubmittedFor}
        />
      </div>
    </SidebarContext.Provider>
  );
});
// : currentEvent?.hasAccess ? (
//               <div className="min-h-full p-4 sm:p-6">
//                 <ErrorBoundary>
//                   <Suspense fallback={<PageLoader />}>{children}</Suspense>
//                 </ErrorBoundary>
//               </div>
// )
export default AppShell;
